import { Core } from "@unseenco/taxi";
import { Transition } from "@lib/page-transitions";
import { Scroll } from "@lib/scroll";
import { Resize } from "@lib/subs";
import {
  createCycles,
  runDestroy,
  runMount,
  runPageOut,
  runPageIn,
} from "@/modules/_";
import { tick } from "@/utils/tick";
import gsap, { reduced, ScrollTrigger } from "@lib/gsap";
import {
  highlightAnimation,
  navigationAnimation,
  setNavActiveState,
} from "@lib/animations";
import { runEmbedScripts } from "./runHubspotEmbeds";

history.scrollRestoration = "manual";

const PAGES_CONFIG = {
  links:
    "a:not([target]):not([href^=\\#]):not([data-taxi-ignore]):not(.w-pagination-wrapper *)",
  removeOldContent: false,
  allowInterruption: false,
  bypassCache: false,
  enablePrefetch: true,
};

const PRIORITY_PAGES = [
  "/contact",
  "/solutions",
  "/blog",
  "/architecture",
  "/clay-services",
  "/gong-services",
];
let embedCleanup: (() => void) | null = null;
let anchorCleanup: (() => void) | null = null;

function setupAnchorLinks(): () => void {
  const links = document.querySelectorAll<HTMLAnchorElement>('a[href^="#"]');
  const controllers: AbortController[] = [];

  links.forEach((link) => {
    const id = link.getAttribute("href")!.slice(1);
    if (!id) return;

    link.removeAttribute("href");
    link.setAttribute("data-taxi-ignore", "");
    link.dataset.anchor = id;
    link.style.cursor = "pointer";

    const controller = new AbortController();
    controllers.push(controller);

    link.addEventListener(
      "click",
      () => {
        const target = document.getElementById(id);
        if (!target) return;
        Scroll.scrollToAnchor(target);
      },
      { signal: controller.signal },
    );
  });

  return () => {
    controllers.forEach((c) => c.abort());
    document
      .querySelectorAll<HTMLAnchorElement>("[data-anchor]")
      .forEach((link) => {
        link.setAttribute("href", `#${link.dataset.anchor}`);
        link.removeAttribute("data-taxi-ignore");
        delete link.dataset.anchor;
        link.style.cursor = "";
      });
  };
}

export interface TransitionParams {
  from?: Element | HTMLElement;
  to?: Element | HTMLElement;
  trigger: HTMLAnchorElement;
  wrapper: HTMLElement | any;
  destination?: string;
}

function resetPage(container: Element) {
  window.scrollTo(0, 0);
  gsap.set(container, {
    clearProps: "position,top,left,width,height,overflowY,zIndex,xPercent",
  });
  Scroll.toTop();
  Scroll.resize();
  Scroll.start();
}

function getOverlay(): { wrap: HTMLDivElement; dark: HTMLDivElement } {
  let wrap = document.querySelector<HTMLDivElement>("[data-transition-wrap]");
  let dark = document.querySelector<HTMLDivElement>("[data-transition-dark]");

  if (!wrap) {
    wrap = document.createElement("div");
    wrap.setAttribute("data-transition-wrap", "");
    Object.assign(wrap.style, {
      position: "fixed",
      inset: "0",
      pointerEvents: "none",
      zIndex: "2",
    });
    document.body.appendChild(wrap);
  }

  if (!dark) {
    dark = document.createElement("div");
    dark.setAttribute("data-transition-dark", "");
    Object.assign(dark.style, {
      position: "absolute",
      inset: "0",
      background: "#000",
    });
    wrap.appendChild(dark);
  }

  gsap.set(dark, { autoAlpha: 0 });
  return { wrap, dark };
}

/* -- Init -- */
export function runInitial() {
  createCycles();
  runMount();
  highlightAnimation();
  navigationAnimation().delay(0.8);
  setNavActiveState();
  runPageIn();
  anchorCleanup = setupAnchorLinks();
  tick.restoreFpsDisplay();
  PRIORITY_PAGES.forEach((url) => Pages.preload(url));
}

export class _Pages extends Core {
  private leavingEl: Element | null = null;

  constructor() {
    super({
      ...PAGES_CONFIG,
      transitions: {
        default: Transition,
      },
    });
  }

  async transitionOut({ from, trigger }: TransitionParams) {
    tick.cleanupFpsDisplay();

    // Close mobile nav if open
    const openMenuBtn = document.querySelector<HTMLElement>(
      ".w-nav-button.w--open",
    );
    if (openMenuBtn) openMenuBtn.click();

    // Cleanup anchor links from previous page
    anchorCleanup?.();
    anchorCleanup = null;

    // Stop smooth scroll during transition
    Scroll.stop();

    await runPageOut();

    // Kill all ScrollTriggers from outgoing page
    ScrollTrigger.getAll().forEach((t) => t.kill());

    // Store reference for overlapping animation in transitionIn
    this.leavingEl = from || null;

    runDestroy();
    Scroll.toTop();
    embedCleanup?.();
    from
      ?.querySelectorAll('.w-embed iframe[id^="hs-form"]')
      .forEach((el) => el.remove());

    if (reduced) {
      gsap.set(from as GSAPTweenTarget, { autoAlpha: 0 });
      this.leavingEl?.remove();
      this.leavingEl = null;
    }
  }

  async transitionIn({ to, trigger }: TransitionParams) {
    createCycles();
    Scroll.resize();
    Resize.force();
    tick.restoreFpsDisplay();

    const leaving = this.leavingEl;
    this.leavingEl = null;

    if (reduced) {
      gsap.set(to as GSAPTweenTarget, { autoAlpha: 1 });
      resetPage(to!);
      await new Promise((resolve) => requestAnimationFrame(resolve));
      ScrollTrigger.refresh();
      embedCleanup = runEmbedScripts(to as HTMLElement);
      runMount();
      highlightAnimation();
      setNavActiveState();
      (window as any).FinsweetAttributes?.modules?.list?.restart();
      await runPageIn();
      anchorCleanup = setupAnchorLinks();
      return;
    }

    const { wrap, dark } = getOverlay();
    wrap.style.display = "";

    // Capture content width BEFORE hiding overflow (scrollbar affects width)
    const contentWidth = document.documentElement.clientWidth;

    // Prevent horizontal scrollbar during slide
    document.documentElement.style.overflow = "hidden";

    // New page: fixed overlay, off-screen — locked to content width
    gsap.set(to as GSAPTweenTarget, {
      position: "fixed",
      top: 0,
      left: 0,
      width: contentWidth,
      height: "100dvh",
      overflowY: "auto",
      xPercent: 100,
      zIndex: 3,
    });

    // Mount modules while page is off-screen so it slides in complete
    runMount();
    highlightAnimation();
    setNavActiveState();
    (window as any).FinsweetAttributes?.modules?.list?.restart();

    const tl = gsap.timeline();
    const tlDur = 1.2;

    // Old page: subtle parallax push left + dark overlay dims it
    if (leaving) {
      tl.to(
        leaving as GSAPTweenTarget,
        { xPercent: -25, duration: tlDur, ease: "parallax", scale: 0.98 },
        0,
      );
      tl.to(dark, { autoAlpha: 0.6, duration: tlDur, ease: "parallax" }, 0);
    }

    // New page: slide in from right to left
    tl.to(
      to as GSAPTweenTarget,
      { xPercent: 0, duration: tlDur, ease: "parallax" },
      0,
    );

    await tl;

    // Cleanup
    leaving?.remove();
    gsap.set(dark, { autoAlpha: 0 });
    wrap.style.display = "none";
    document.documentElement.style.overflow = "";
    resetPage(to!);

    // Wait one frame for reflow after clearProps, then refresh all ScrollTriggers
    await new Promise((resolve) => requestAnimationFrame(resolve));
    ScrollTrigger.refresh();

    // Run embed scripts after page is settled so HubSpot forms initialise in final layout
    embedCleanup = runEmbedScripts(to as HTMLElement);

    // Fire page-in animations now that the page is settled
    await runPageIn();
    anchorCleanup = setupAnchorLinks();
  }
}

export const Pages = new _Pages();
